import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  checkInMeta,
  fetchBookings,
  typeLabel,
  updateCheckInStatus,
  type Booking,
  type CheckInStatus,
} from "@/lib/bookings";
import { exportToCsv, exportToPdf, type ExportColumn } from "@/pages/admin/exportUtils";
import { toISODate } from "@/pages/admin/format";

const exportColumns: ExportColumn<Booking>[] = [
  { header: "Booking ID", value: (booking) => `RTG-${10000 + booking.id}` },
  { header: "Visitor", value: (booking) => booking.name },
  { header: "Contact", value: (booking) => booking.phone || booking.email },
  { header: "Farm / Experience", value: (booking) => booking.subject || "" },
  { header: "Guests", value: (booking) => (booking.group_size ? String(booking.group_size) : "") },
  { header: "Arrival", value: (booking) => booking.arrival_time || "" },
  { header: "Status", value: (booking) => checkInMeta(booking.check_in_status).label },
];

const exportBtn =
  "inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer";

function timeToMinutes(value: string | null): number {
  if (!value) return 24 * 60;
  const match = value.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!match) return 24 * 60;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const ampm = match[3]?.toUpperCase();
  if (ampm === "PM" && hours < 12) hours += 12;
  if (ampm === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

export default function AdminCheckIns() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [toast, setToast] = useState("");
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setBookings(await fetchBookings());
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn't load check-ins. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const arrivals = useMemo(() => {
    return bookings
      .filter((booking) => (booking.preferred_date || "").slice(0, 10) === date && booking.status !== "cancelled")
      .sort((a, b) => timeToMinutes(a.arrival_time) - timeToMinutes(b.arrival_time));
  }, [bookings, date]);

  const count = (status: CheckInStatus) =>
    arrivals.filter((booking) => (booking.check_in_status || "awaiting") === status).length;

  const stats = [
    { label: "Expected today", value: arrivals.length, icon: "ri-calendar-check-line", tone: "primary" },
    { label: "Checked in", value: count("checked_in"), icon: "ri-checkbox-circle-line", tone: "secondary" },
    { label: "Awaiting", value: count("awaiting"), icon: "ri-time-line", tone: "accent" },
    { label: "No-shows", value: count("no_show"), icon: "ri-user-unfollow-line", tone: "primary" },
  ];

  const toneBubble: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-900",
    secondary: "bg-secondary-100 text-secondary-700",
  };

  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 3000);
  };

  const handleStatus = async (booking: Booking, status: CheckInStatus) => {
    const previous = booking.check_in_status;
    setBookings((list) =>
      list.map((item) =>
        item.id === booking.id
          ? {
              ...item,
              check_in_status: status,
              checked_in_at: status === "checked_in" ? new Date().toISOString() : null,
            }
          : item,
      ),
    );
    try {
      await updateCheckInStatus(booking.id, status);
      if (status === "checked_in") notify(`${booking.name} checked in.`);
      else if (status === "no_show") notify(`${booking.name} marked as no-show.`);
      else notify(`${booking.name} reset to awaiting.`);
    } catch {
      setBookings((list) =>
        list.map((item) =>
          item.id === booking.id ? { ...item, check_in_status: previous } : item,
        ),
      );
      setError("We couldn't update that check-in. Please try again.");
    }
  };

  const prettyDate = new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 md:mb-8">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
            Operations
          </span>
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-2">
            Check-ins
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-2xl">
            Greet every arrival, mark guests in as they reach the farm, and record no-shows.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => exportToCsv(`rythugaru-check-ins-${date}`, exportColumns, arrivals)}
            className={exportBtn}
          >
            <i className="ri-file-excel-2-line"></i>
            CSV
          </button>
          <button
            type="button"
            onClick={() =>
              exportToPdf({
                title: "Check-ins",
                subtitle: `Arrivals for ${prettyDate}`,
                columns: exportColumns,
                rows: arrivals,
              })
            }
            className={exportBtn}
          >
            <i className="ri-file-pdf-2-line"></i>
            PDF
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setQrOpen(true)}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-qr-scan-2-line"></i>
            QR check-in
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3.5 mb-5">
          <p className="flex items-start gap-2 text-sm text-accent-900 flex-1">
            <i className="ri-error-warning-line text-lg"></i>
            <span>{error}</span>
          </p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer self-start"
          >
            <i className="ri-refresh-line"></i> Retry
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            <span className={`w-10 h-10 flex items-center justify-center rounded-full ${toneBubble[stat.tone]}`}>
              <i className={`${stat.icon} text-xl`}></i>
            </span>
            <p className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950 mt-4">
              {stat.value}
            </p>
            <p className="text-xs md:text-sm text-foreground-600 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-5 bg-background-50 border border-background-200 rounded-lg p-4">
        <span className="inline-flex items-center gap-2 text-sm text-foreground-700">
          <i className="ri-calendar-line text-primary-600"></i>
          Arrivals for
        </span>
        <input
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          className="bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
        />
        <span className="text-sm text-foreground-500">{prettyDate}</span>
        <span className="sm:ml-auto text-xs text-foreground-500">
          {arrivals.length} {arrivals.length === 1 ? "arrival" : "arrivals"}
        </span>
      </div>

      {loading ? (
        <div className="bg-background-50 border border-background-200 rounded-lg py-20 mt-5 flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
          <p className="text-sm text-foreground-600">Loading arrivals…</p>
        </div>
      ) : arrivals.length === 0 ? (
        <div className="bg-background-50 border border-background-200 rounded-lg py-16 px-6 text-center mt-5">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-calendar-check-line text-3xl"></i>
          </span>
          <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
            No arrivals on this date
          </h3>
          <p className="text-foreground-600 mt-3 max-w-md mx-auto">
            Pick another date, or check the calendar to see where bookings are landing.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div className="hidden md:block bg-background-50 border border-background-200 rounded-lg overflow-hidden mt-5">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-background-100 border-b border-background-200">
                    {["Booking", "Visitor", "Farm / Experience", "Guests", "Arrival", "Status", "Actions"].map((head) => (
                      <th
                        key={head}
                        className="px-4 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500 whitespace-nowrap"
                      >
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {arrivals.map((booking) => {
                    const meta = checkInMeta(booking.check_in_status);
                    return (
                      <tr key={booking.id} className="hover:bg-background-100 transition-colors">
                        <td className="px-4 py-4">
                          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                            RTG-{10000 + booking.id}
                          </p>
                          <p className="text-[11px] text-foreground-500 mt-0.5">{typeLabel(booking.booking_type)}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{booking.name}</p>
                          <p className="text-xs text-foreground-500 mt-0.5">{booking.phone || booking.email}</p>
                        </td>
                        <td className="px-4 py-4 text-sm text-foreground-700 max-w-[220px] truncate">
                          {booking.subject || "—"}
                        </td>
                        <td className="px-4 py-4 text-sm text-foreground-700">
                          {booking.group_size ? `${booking.group_size}` : "—"}
                        </td>
                        <td className="px-4 py-4 text-sm text-foreground-700 whitespace-nowrap">
                          {booking.arrival_time || "—"}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${meta.badge}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {booking.check_in_status === "checked_in" ? (
                              <button
                                type="button"
                                onClick={() => handleStatus(booking, "awaiting")}
                                className="text-xs font-semibold text-foreground-600 px-3 py-2 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                Undo
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleStatus(booking, "checked_in")}
                                  className="inline-flex items-center gap-1.5 bg-primary-500 text-background-50 text-xs font-semibold px-3 py-2 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                                >
                                  <i className="ri-checkbox-circle-line"></i> Check in
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleStatus(booking, "no_show")}
                                  className="text-xs font-semibold text-foreground-600 px-3 py-2 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                                >
                                  No-show
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden space-y-3 mt-5">
            {arrivals.map((booking) => {
              const meta = checkInMeta(booking.check_in_status);
              return (
                <div key={booking.id} className="bg-background-50 border border-background-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] text-foreground-500">RTG-{10000 + booking.id}</p>
                      <p className="text-sm font-semibold text-foreground-950 mt-0.5">{booking.name}</p>
                      <p className="text-xs text-foreground-500 mt-0.5 truncate">{booking.subject || "—"}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${meta.badge}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                      {meta.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-group-line"></i>
                      {booking.group_size ? `${booking.group_size} guests` : "—"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {booking.arrival_time || "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-background-200">
                    {booking.check_in_status === "checked_in" ? (
                      <button
                        type="button"
                        onClick={() => handleStatus(booking, "awaiting")}
                        className="text-xs font-semibold text-foreground-600 px-3 py-2 rounded-md hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        Undo check-in
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => handleStatus(booking, "checked_in")}
                          className="inline-flex items-center gap-1.5 bg-primary-500 text-background-50 text-xs font-semibold px-3 py-2 rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-checkbox-circle-line"></i> Check in
                        </button>
                        <button
                          type="button"
                          onClick={() => handleStatus(booking, "no_show")}
                          className="text-xs font-semibold text-foreground-600 px-3 py-2 rounded-md hover:bg-background-200 transition-colors cursor-pointer"
                        >
                          No-show
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="mt-5">
        <Link
          to="/admin/bookings"
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-line"></i>
          Manage all bookings
        </Link>
      </div>

      {qrOpen ? (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/60" onClick={() => setQrOpen(false)} aria-hidden="true"></div>
          <div
            className="relative w-full max-w-sm bg-background-50 border border-background-200 rounded-lg p-6 text-center"
            role="dialog"
            aria-modal="true"
            aria-label="QR check-in"
          >
            <button
              type="button"
              onClick={() => setQrOpen(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-qr-scan-2-line text-3xl"></i>
            </span>
            <h2 className="font-heading text-xl text-foreground-950 font-semibold mt-4">QR check-in</h2>
            <p className="text-sm text-foreground-600 mt-2">
              Each confirmed guest gets a QR code. Point it at the farm-gate scanner and the check-in
              records here automatically.
            </p>
            <div className="mt-5 border-2 border-dashed border-background-300 rounded-lg py-8 flex flex-col items-center gap-2">
              <i className="ri-qr-code-line text-4xl text-foreground-300"></i>
              <span className="text-xs text-foreground-500">Scanner ready</span>
            </div>
            <button
              type="button"
              onClick={() => {
                setQrOpen(false);
                notify("Scanner mode is a Phase 6 enhancement.");
              }}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-5 py-3 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-camera-line"></i>
              Open scanner
            </button>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120] max-w-[90vw]">
          <div className="flex items-center gap-3 bg-foreground-950 text-background-50 text-sm px-4 py-3 rounded-lg">
            <i className="ri-checkbox-circle-line text-accent-400 text-lg"></i>
            <span>{toast}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}